import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEquipmentDetail } from './hooks/useEquipmentDetail';
import { cn } from '@/utils/cn';

export function EquipmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { equipment, logs, isLoading } = useEquipmentDetail(id || '');

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-40 rounded-md bg-white border border-slate-100" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           <div className="h-32 rounded-md bg-white border border-slate-100" />
           <div className="h-32 rounded-md bg-white border border-slate-100" />
           <div className="h-32 rounded-md bg-white border border-slate-100" />
        </div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="py-20 text-center">
        <i className="ri-error-warning-line text-4xl text-slate-300" />
        <p className="mt-2 sm:text-sm text-xs font-normal text-slate-500">Không tìm thấy thông tin thiết bị</p>
        <button onClick={() => navigate('/equipment')} className="mt-4 text-emerald-600 font-normal hover:underline sm:text-sm text-xs">Quay lại danh sách</button>
      </div>
    );
  }

  const renderStatusBadge = (status: string) => {
    const configs: Record<string, { label: string; className: string }> = {
      available: { label: 'Sẵn sàng', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
      in_use: { label: 'Đang sử dụng', className: 'bg-blue-50 text-blue-600 border-blue-100' },
      maintenance: { label: 'Bảo trì', className: 'bg-amber-50 text-amber-600 border-amber-100' },
    };
    const config = configs[status] || { label: status, className: 'bg-slate-50 text-slate-500 border-slate-100' };
    return (
      <span className={cn("inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-normal  tracking-tight", config.className)}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="mx-auto max-w-screen-2xl space-y-4 pb-10">
      {/* Header Block */}
      <div className="rounded-md bg-white p-4 border border-slate-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-md bg-slate-50 text-slate-400 overflow-hidden border border-slate-100">
             {equipment.thumbnail_url ? <img src={equipment.thumbnail_url} className="h-full w-full object-cover" /> : <i className="ri-tools-line text-3xl" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
               <h2 className="text-xl font-normal text-slate-900">{equipment.name}</h2>
               {renderStatusBadge(equipment.status_code)}
            </div>
            <p className="sm:text-sm text-xs font-normal text-slate-500">{equipment.code} • {equipment.group?.name || 'Chưa phân nhóm'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <button className="h-10 rounded-md border border-slate-200 px-4 sm:text-sm text-xs font-normal text-slate-600 hover:bg-slate-50 transition-all">Lịch bảo trì</button>
           <button className="h-10 rounded-md bg-slate-900 px-4 sm:text-sm text-xs font-normal text-white hover:bg-slate-800 transition-all">Tạo yêu cầu sửa chữa</button>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-md bg-white p-4 border border-slate-100 space-y-3">
           <span className="text-[10px] font-normal text-slate-400 tracking-widest">Vị trí hiện tại</span>
           <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-600">
                 <i className="ri-map-pin-2-line text-xl" />
              </div>
              <div className="flex flex-col">
                 <span className="sm:text-sm text-xs font-normal text-slate-700">{equipment.project?.name || 'Kho trung tâm'}</span>
                 <span className="text-[10px] font-normal text-slate-400">{equipment.current_location || 'Đang tại kho'}</span>
              </div>
           </div>
        </div>

        <div className="rounded-md bg-white p-4 border border-slate-100 space-y-3">
           <span className="text-[10px] font-normal text-slate-400 tracking-widest">Người phụ trách</span>
           <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 border border-slate-200 overflow-hidden text-slate-400">
                 {equipment.assigned_to?.avatar_path ? <img src={equipment.assigned_to.avatar_path} className="h-full w-full object-cover" /> : <i className="ri-user-line text-xl" />}
              </div>
              <div className="flex flex-col">
                 <span className="sm:text-sm text-xs font-normal text-slate-700">{equipment.assigned_to?.full_name || 'Chưa bàn giao'}</span>
                 <span className="text-[10px] font-normal text-emerald-600 tracking-tight">Vận hành viên</span>
              </div>
           </div>
        </div>

        <div className="rounded-md bg-white p-4 border border-slate-100 space-y-3">
           <span className="text-[10px] font-normal text-slate-400 tracking-widest">Tình trạng vật lý</span>
           <div className="flex items-center gap-2">
              <span className="text-2xl font-normal text-slate-900">{equipment.condition_code || '---'}</span>
              <div className="h-2 w-full max-w-[100px] rounded-full bg-slate-100 overflow-hidden">
                 <div className={cn("h-full rounded-full", equipment.condition_code === 'excellent' ? 'bg-emerald-500 w-full' : 'bg-amber-500 w-3/4')} />
              </div>
           </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
           <div className="rounded-md bg-white p-4 border border-slate-100 space-y-4">
              <h3 className="sm:text-sm text-xs font-normal text-slate-400 tracking-widest border-b border-slate-50 pb-2">Thông số kỹ thuật</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                 {[
                   { label: 'Thương hiệu', value: equipment.brand },
                   { label: 'Model', value: equipment.model },
                   { label: 'Số Serial', value: equipment.serial_number },
                   { label: 'Ngày mua', value: equipment.purchase_date ? new Date(equipment.purchase_date).toLocaleDateString('vi-VN') : '---' },
                   { label: 'Hết hạn bảo hành', value: equipment.warranty_expiry ? new Date(equipment.warranty_expiry).toLocaleDateString('vi-VN') : '---' },
                   { label: 'Giá trị mua', value: equipment.purchase_price ? `${equipment.purchase_price.toLocaleString()} VNĐ` : '---' },
                 ].map((item, i) => (
                   <div key={i} className="space-y-1">
                      <p className="text-[10px] font-normal text-slate-400">{item.label}</p>
                      <p className="sm:text-sm text-xs font-normal text-slate-700">{item.value || '---'}</p>
                   </div>
                 ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-50">
                 <p className="text-[10px] font-normal text-slate-400 mb-2">Đặc tính kỹ thuật</p>
                 <p className="sm:text-sm text-xs text-slate-600 leading-relaxed">{equipment.specification || 'Chưa cập nhật thông tin kỹ thuật chi tiết.'}</p>
              </div>
           </div>

           <div className="rounded-md bg-white p-4 border border-slate-100 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-50 pb-2">
                 <h3 className="sm:text-sm text-xs font-normal text-slate-400 tracking-widest">Nhật ký sử dụng gần đây</h3>
                 <button className="text-[10px] font-normal text-emerald-600 hover:underline">XEM TẤT CẢ</button>
              </div>
              <div className="divide-y divide-slate-50">
                 {logs.length === 0 ? (
                   <div className="py-10 text-center text-slate-400 sm:text-sm text-xs italic">Chưa có nhật ký sử dụng</div>
                 ) : logs.map((log: any) => (
                   <div key={log.id} className="py-3 flex items-center justify-between">
                      <div className="flex flex-col">
                         <span className="sm:text-sm text-xs font-normal text-slate-700">{log.project?.name || 'Dự án không xác định'}</span>
                         <span className="text-[10px] font-normal text-slate-400">{new Date(log.start_time).toLocaleString('vi-VN')}</span>
                      </div>
                      <div className="text-right">
                         <span className="sm:text-sm text-xs font-normal text-slate-900">{log.usage_hours || 0} giờ</span>
                         <p className="text-[10px] font-normal text-emerald-600 tracking-tight">{log.user?.full_name}</p>
                      </div>
                   </div>
                 ))}
              </div>
           </div>
        </div>

        <div className="lg:col-span-4 space-y-4">
           <div className="rounded-md bg-white p-4 border border-slate-100 space-y-4">
              <h3 className="sm:text-sm text-xs font-normal text-slate-400 tracking-widest border-b border-slate-50 pb-2">Tình trạng bảo trì</h3>
              <div className="flex flex-col gap-4">
                 <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-md border border-slate-100">
                    <span className="text-[10px] font-normal text-slate-400 tracking-widest mb-1">Dự kiến bảo trì tiếp theo</span>
                    <span className="text-lg font-normal text-slate-900">25/06/2024</span>
                    <span className="text-[10px] font-normal text-amber-600 tracking-tight mt-1">Còn 22 ngày</span>
                 </div>
                 <div className="space-y-2">
                    <p className="text-[10px] font-normal text-slate-400">Cấp độ bảo trì</p>
                    <div className="flex items-center gap-2">
                       <span className="h-2 w-2 rounded-full bg-blue-500" />
                       <span className="sm:text-sm text-xs font-normal text-slate-700">Định kỳ Cấp 2 (500 giờ)</span>
                    </div>
                 </div>
                 <button className="w-full h-10 rounded-md bg-emerald-700 text-white sm:text-sm text-xs font-normal hover:bg-emerald-800 transition-all -emerald-700/10">Lập lịch bảo trì ngay</button>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}

export default EquipmentDetailPage;
